/**
 * TerrainTileSource — laedt und dekodiert Hoehen- und Orthofoto-Kacheln.
 *
 * Hoehen: Terrain-RGB-PNG (Mapbox-Kodierung, hoehe_m = -10000 + wert*0.1),
 * erzeugt von map-capture/terrain/heightmap-kacheln.py — deckungsgleich mit
 * dem Orthofoto-Baum. Orthos: ab orthoDetailAbZoom ein z+2-Komposit.
 *
 * Performance-Architektur (Debugger-Report #4729):
 * - P1/P2: Decode + Komposit laufen in einem WORKER-POOL (fetch →
 *   createImageBitmap → OffscreenCanvas) — der Main-Thread bekommt nur
 *   Transferables (Float32Array / ImageBitmap). Vorher war allein
 *   `getImageData` des Hoehen-Decodes 58 % der gesamten CPU-Zeit.
 * - P0: Jeder Load ist per AbortSignal abbrechbar (Kachel verlaesst den
 *   Bedarf → cancel), eine gemeinsame Parallelitaets-Schleuse deckelt
 *   Hoehen UND Orthos, und ein Bounds-Manifest (kachel-bounds.json)
 *   verhindert Requests ausserhalb des Baums (vorher 600–1.500 404s/min).
 * - Fallback: ohne Worker/OffscreenCanvas (sehr alte Browser) laeuft der
 *   bisherige Main-Thread-Pfad unveraendert.
 */
import * as THREE from 'three';
import type { TileAddress } from './MercatorProjection';
import type { HoehenAntwort, OrthoAntwort } from './TerrainDecodeWorker';

export interface Hoehenfeld {
    /** Kantenpunkte (aufloesung x aufloesung), Zeile 0 = Nordrand */
    daten: Float32Array;
    aufloesung: number;
    min: number;
    max: number;
}

export interface TerrainTileSourceConfig {
    /** z. B. https://…/kaernten/terrain/{z}/{x}/{y}.png */
    hoehenUrl: string;
    /** z. B. https://…/kaernten/tiles/{z}/{x}/{y}.jpg */
    orthoUrl: string;
    /** Kachel-Existenzbereiche je Zoom ({hoehen:{z:[x0,y0,x1,y1]},ortho:{…}}) */
    boundsUrl?: string;
    /** Vertex-Aufloesung je Kachelkante (Vorgabe 65 → 64x64 Quads) */
    meshAufloesung?: number;
    maxParallel?: number;
    lruGroesse?: number;
    /**
     * Ortho-Detail: ab dieser Terrain-Zoomstufe werden Sub-Orthos von
     * z+orthoDetailOffset zu EINER scharfen Textur komponiert (der
     * Orthofoto-Baum reicht tiefer als das Terrain — z. B. z19 in der
     * Schlucht bei Terrain-z14). Fehlende Sub-Kacheln (ausserhalb der
     * Abdeckung) deckt die Basis-Kachel als Untergrund ab — nie Loecher.
     */
    orthoDetailOffset?: number;
    orthoDetailAbZoom?: number;
}

export interface OrthoErgebnis {
    tex: THREE.Texture;
    /** Texturkante in px — fuer den Halb-Pixel-UV-Inset gegen Naht-Bleeding */
    groesse: number;
}

type BoundsJeZoom = Record<string, [number, number, number, number]>;

const fuelle = (vorlage: string, t: TileAddress) =>
    vorlage.replace('{z}', String(t.z)).replace('{x}', String(t.x)).replace('{y}', String(t.y));

const schluessel = (t: TileAddress) => `${t.z}/${t.x}/${t.y}`;

/* Sicherheitsrand von einer Kachel um die Manifest-Bounds.
 *
 * Anlass (2026-08-17): Ein zu klein gescanntes Manifest liess reale Kacheln
 * ungefragt — das Terrain blieb dort ohne Textur (Alex-Screenshot 119758).
 * Ein falsches Manifest darf nie Inhalt verschlucken; der Rand kostet im
 * schlechtesten Fall ein paar 404s, die Queue faengt sie ohnehin ab. */
const RAND = 1;

const inBounds = (b: BoundsJeZoom | null, t: TileAddress): boolean => {
    if (!b) return true;                        // ohne Manifest: nichts ausschliessen
    const z = b[String(t.z)];
    if (!z) return false;                       // Zoomstufe existiert nicht im Baum
    return t.x >= z[0] - RAND && t.y >= z[1] - RAND && t.x <= z[2] + RAND && t.y <= z[3] + RAND;
};

export class TerrainTileSource {
    readonly cfg: Required<TerrainTileSourceConfig>;
    private _hoehenCache = new Map<string, Hoehenfeld>();          // LRU (Map-Ordnung)
    private _laufend = new Map<string, Promise<Hoehenfeld | null>>();
    private _aktiv = 0;
    private _warteschlange: (() => void)[] = [];
    private _decodeCanvas: HTMLCanvasElement | null = null;

    private _hoehenBounds: BoundsJeZoom | null = null;
    private _orthoBounds: BoundsJeZoom | null = null;

    private _worker: Worker[] | null = null;
    private _naechsterWorker = 0;
    private _jobId = 0;
    private _jobs = new Map<number, { resolve: (antwort: HoehenAntwort & OrthoAntwort) => void; worker: Worker }>();

    constructor(cfg: TerrainTileSourceConfig) {
        this.cfg = {
            /* 129 = 128x128 Quads je Kachel (~19 m Schritt bei z14). 65 war
             * am Steilhang eine Treppe (Alex-Screenshot 119428). */
            meshAufloesung: 129,
            maxParallel: 8,
            lruGroesse: 300,
            orthoDetailOffset: 2,
            orthoDetailAbZoom: 12,
            boundsUrl: '',
            ...cfg,
        };
        if (this.cfg.boundsUrl) {
            void fetch(this.cfg.boundsUrl)
                .then((r) => (r.ok ? r.json() : null))
                .then((doc) => {
                    this._hoehenBounds = doc?.hoehen ?? null;
                    this._orthoBounds = doc?.ortho ?? null;
                })
                .catch(() => undefined);        // Manifest ist Optimierung, nie Blocker
        }
        this._starteWorker();
    }

    /** Worker-Pool (2–3 je nach Kernen); scheitert leise → Main-Thread-Pfad. */
    private _starteWorker(): void {
        if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return;
        try {
            const anzahl = Math.max(2, Math.min(3, (navigator.hardwareConcurrency || 4) - 2));
            const pool: Worker[] = [];
            for (let i = 0; i < anzahl; i++) {
                const w = new Worker(new URL('./TerrainDecodeWorker.ts', import.meta.url), { type: 'module' });
                w.onmessage = (e: MessageEvent<HoehenAntwort & OrthoAntwort>) => {
                    const eintrag = this._jobs.get(e.data.id);
                    if (!eintrag) return;
                    this._jobs.delete(e.data.id);
                    eintrag.resolve(e.data);
                };
                pool.push(w);
            }
            this._worker = pool;
        } catch {
            this._worker = null;
        }
    }

    private _imWorker<T extends HoehenAntwort & OrthoAntwort>(
        job: Record<string, unknown>, signal?: AbortSignal,
    ): Promise<T> {
        const id = ++this._jobId;
        const worker = this._worker![this._naechsterWorker++ % this._worker!.length];
        return new Promise<T>((resolve) => {
            this._jobs.set(id, { resolve: resolve as (a: HoehenAntwort & OrthoAntwort) => void, worker });
            signal?.addEventListener('abort', () => {
                worker.postMessage({ typ: 'abbruch', id });
            }, { once: true });
            worker.postMessage({ ...job, id });
        });
    }

    /** Hoehenfeld einer Kachel (dedupliziert, LRU-gecacht); null bei 404/Abbruch. */
    ladeHoehen(t: TileAddress, signal?: AbortSignal): Promise<Hoehenfeld | null> {
        if (!inBounds(this._hoehenBounds, t)) return Promise.resolve(null);
        const k = schluessel(t);
        const gecacht = this._hoehenCache.get(k);
        if (gecacht) {
            // LRU-Auffrischung
            this._hoehenCache.delete(k);
            this._hoehenCache.set(k, gecacht);
            return Promise.resolve(gecacht);
        }
        const laufend = this._laufend.get(k);
        if (laufend) return laufend;

        const versprechen = this._mitSlot(() => this._ladeHoehenRoh(t, signal), signal)
            .then((feld) => {
                if (feld) {
                    this._hoehenCache.set(k, feld);
                    while (this._hoehenCache.size > this.cfg.lruGroesse) {
                        const aeltester = this._hoehenCache.keys().next().value as string;
                        this._hoehenCache.delete(aeltester);
                    }
                }
                return feld;
            })
            .finally(() => this._laufend.delete(k));
        this._laufend.set(k, versprechen);
        return versprechen;
    }

    private async _ladeHoehenRoh(t: TileAddress, signal?: AbortSignal): Promise<Hoehenfeld | null> {
        /* Adaptive Mesh-Dichte: volle Aufloesung nur fuer tiefe Nah-Kacheln. */
        const n = t.z >= 14 ? this.cfg.meshAufloesung : Math.min(65, this.cfg.meshAufloesung);
        if (this._worker) {
            const antwort = await this._imWorker<HoehenAntwort>(
                { typ: 'hoehen', url: fuelle(this.cfg.hoehenUrl, t), n }, signal,
            );
            if (!antwort.daten || antwort.fehlt || antwort.abgebrochen) return null;
            return { daten: antwort.daten, aufloesung: antwort.aufloesung!, min: antwort.min!, max: antwort.max! };
        }
        return this._ladeHoehenMainThread(t, n);
    }

    /** Orthofoto als THREE.Texture (ggf. z+n-Detail-Komposit); null bei 404/Abbruch. */
    async ladeOrtho(t: TileAddress, signal?: AbortSignal): Promise<OrthoErgebnis | null> {
        if (!inBounds(this._orthoBounds, t)) return null;
        const offset = this.cfg.orthoDetailOffset;
        const willDetail = offset > 0 && t.z >= this.cfg.orthoDetailAbZoom;
        const teiler = 2 ** offset;
        const groesse = willDetail ? Math.min(1024, 256 * teiler) : 256;
        const zelle = groesse / teiler;

        // Sub-Kacheln ausserhalb des Baums GAR NICHT anfragen (P0: 404-Sturm)
        const subs = !willDetail ? [] : Array.from({ length: teiler * teiler }, (_, i) => {
            const dx = i % teiler, dy = Math.floor(i / teiler);
            const addr = { z: t.z + offset, x: t.x * teiler + dx, y: t.y * teiler + dy };
            return inBounds(this._orthoBounds, addr)
                ? { url: fuelle(this.cfg.orthoUrl, addr), dx, dy }
                : null;
        }).filter((s): s is { url: string; dx: number; dy: number } => s !== null);

        if (this._worker) {
            return this._mitSlot(async () => {
                const antwort = await this._imWorker<OrthoAntwort>({
                    typ: 'ortho',
                    basisUrl: fuelle(this.cfg.orthoUrl, t),
                    subs, groesse, zelle,
                }, signal);
                if (!antwort.bitmap || antwort.fehlt || antwort.abgebrochen) return null;
                const tex = new THREE.Texture(antwort.bitmap);
                return this._konfiguriere(tex, antwort.groesse ?? groesse);
            }, signal);
        }
        return this._mitSlot(() => this._ladeOrthoMainThread(t, subs, willDetail, groesse, zelle), signal);
    }

    // ── Main-Thread-Fallback (Browser ohne Worker/OffscreenCanvas) ──────

    private async _ladeOrthoMainThread(
        t: TileAddress, subs: { url: string; dx: number; dy: number }[],
        willDetail: boolean, groesse: number, zelle: number,
    ): Promise<OrthoErgebnis | null> {
        const basis = await this._ladeBild(fuelle(this.cfg.orthoUrl, t));
        if (!willDetail) {
            return basis ? this._konfiguriere(new THREE.Texture(basis), basis.width || 256) : null;
        }
        const bilder = await Promise.all(
            subs.map((s) => this._ladeBild(s.url).then((bild) => ({ ...s, bild }))),
        );
        if (!basis && bilder.every((s) => !s.bild)) return null;
        const c = document.createElement('canvas');
        c.width = c.height = groesse;
        const g = c.getContext('2d')!;
        g.imageSmoothingEnabled = true;
        if (basis) g.drawImage(basis, 0, 0, groesse, groesse);
        for (const { dx, dy, bild } of bilder) {
            if (bild) g.drawImage(bild, dx * zelle, dy * zelle, zelle, zelle);
        }
        return this._konfiguriere(new THREE.CanvasTexture(c), groesse);
    }

    private async _ladeHoehenMainThread(t: TileAddress, n: number): Promise<Hoehenfeld | null> {
        const bild = await this._ladeBild(fuelle(this.cfg.hoehenUrl, t));
        if (!bild) return null;
        const q = bild.width || 256;
        if (!this._decodeCanvas) this._decodeCanvas = document.createElement('canvas');
        const c = this._decodeCanvas;
        c.width = q; c.height = q;
        const g = c.getContext('2d', { willReadFrequently: true })!;
        g.imageSmoothingEnabled = false;
        g.drawImage(bild, 0, 0);
        const px = g.getImageData(0, 0, q, q).data;
        const roh = new Float32Array(q * q);
        for (let i = 0; i < q * q; i++) {
            const o = i * 4;
            roh[i] = -10000 + (px[o] * 65536 + px[o + 1] * 256 + px[o + 2]) * 0.1;
        }
        const daten = new Float32Array(n * n);
        let min = Infinity, max = -Infinity;
        for (let j = 0; j < n; j++) {
            const fy = (j / (n - 1)) * (q - 1);
            const y0 = Math.min(q - 2, Math.floor(fy)), ty = fy - y0;
            for (let i = 0; i < n; i++) {
                const fx = (i / (n - 1)) * (q - 1);
                const x0 = Math.min(q - 2, Math.floor(fx)), tx = fx - x0;
                const h = (roh[y0 * q + x0] * (1 - tx) + roh[y0 * q + x0 + 1] * tx) * (1 - ty)
                    + (roh[(y0 + 1) * q + x0] * (1 - tx) + roh[(y0 + 1) * q + x0 + 1] * tx) * ty;
                daten[j * n + i] = h;
                if (h < min) min = h;
                if (h > max) max = h;
            }
        }
        return { daten, aufloesung: n, min, max };
    }

    private _konfiguriere(tex: THREE.Texture, groesse: number): OrthoErgebnis {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.flipY = false;
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.anisotropy = 4;
        tex.needsUpdate = true;
        return { tex, groesse };
    }

    private _ladeBild(url: string): Promise<HTMLImageElement | null> {
        return new Promise((resolve) => {
            const bild = new Image();
            bild.crossOrigin = 'anonymous';
            bild.onload = () => resolve(bild);
            bild.onerror = () => resolve(null);
            bild.src = url;
        });
    }

    /** Parallelitaets-Schleuse; abgebrochene Wartende starten gar nicht erst. */
    private async _mitSlot<T>(arbeit: () => Promise<T>, signal?: AbortSignal): Promise<T | null> {
        if (this._aktiv >= this.cfg.maxParallel) {
            await new Promise<void>((r) => this._warteschlange.push(r));
        }
        if (signal?.aborted) {
            this._warteschlange.shift()?.();
            return null;
        }
        this._aktiv++;
        try {
            return await arbeit();
        } finally {
            this._aktiv--;
            this._warteschlange.shift()?.();
        }
    }

    dispose(): void {
        for (const w of this._worker ?? []) w.terminate();
        this._worker = null;
        this._jobs.clear();
    }
}
